import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import McpServerIcon from "./McpServerIcon";

/**
 * Centered gate shown when turning on / running a scheduled task that requires
 * MCP servers the user hasn't connected. Lists the missing connectors with
 * Connect buttons (the standard OAuth popup flow); "Turn on task" unlocks only
 * once `servers` (live state from useMcpServers) shows them all connected.
 */
export default function McpRequiredModal({
  missing, servers, action = "enable", onConnect, onRetry, onClose,
  title = "Connect required servers",
  hint = "This task needs the following connectors before it can run.",
  actionLabel,
  autoProceed = false,      // fire onRetry on its own once everything is connected
  onContinueAnyway,         // optional red escape hatch
}) {
  const [busyUrl, setBusyUrl] = useState(null);
  const [err, setErr] = useState(null);

  const byUrl = new Map(servers.map((s) => [s.url, s]));
  const isReady = (url) => {
    const s = byUrl.get(url);
    return !!(s && s.connected && !s.needs_reauth);
  };
  const allReady = missing.every((m) => isReady(m.url));
  useEffect(() => { if (autoProceed && allReady) onRetry(); }, [autoProceed, allReady, onRetry]);

  const connect = async (url) => {
    setBusyUrl(url); setErr(null);
    try { await onConnect(url); }
    catch (e) { setErr(e.message || "Failed to connect"); }
    finally { setBusyUrl(null); }
  };

  return createPortal(
    <div className="ca-mcp-modal-backdrop" onClick={onClose}>
      <div className="ca-mcp-modal" onClick={(e) => e.stopPropagation()}>
        <h3>{title}</h3>
        <p className="ca-sched-skill-hint">{hint}</p>
        <ul className="ca-mcp-list">
          {missing.map((m) => {
            const s = byUrl.get(m.url);
            return (
              <li key={m.url} className="ca-mcp-row">
                <div className="ca-mcp-row-top">
                  <div className="ca-mcp-meta">
                    {s && <McpServerIcon url={s.url} hasIcon={s.has_icon}
                                         iconVersion={s.icon_version} />}
                    <span className="ca-mcp-name" title={m.url}>{m.name}</span>
                  </div>
                  {isReady(m.url) ? (
                    <span className="ca-mcp-badge">Connected</span>
                  ) : (
                    <button
                      className="ca-btn ca-btn--xs ca-btn--primary"
                      disabled={busyUrl === m.url}
                      onClick={() => connect(m.url)}
                    >
                      {busyUrl === m.url ? "Connecting…"
                        : s?.connected && s.needs_reauth ? "Reconnect" : "Connect"}
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
        {err && <div className="ca-mcp-modal-err">{err}</div>}
        <div className="ca-mcp-modal-actions">
          {onContinueAnyway && (
            <button type="button" className="ca-btn ca-btn--sm"
                    style={{ marginRight: "auto", color: "var(--ca-danger)" }}
                    onClick={onContinueAnyway}>
              Continue anyway
            </button>
          )}
          <button type="button" className="ca-btn ca-btn--sm" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="ca-btn ca-btn--sm ca-btn--primary"
                  disabled={!allReady} onClick={onRetry}>
            {actionLabel || (action === "run" ? "Run now" : "Turn on task")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
